import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

export type TimeRange = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'all';

export interface TimeRangeSelectorProps {
  value?: TimeRange;
  onChange?: (range: TimeRange) => void;
  options?: TimeRange[];
  variant?: 'default' | 'compact';
}

const defaultOptions: TimeRange[] = ['today', 'week', 'month', 'quarter', 'year', 'all'];

const CalendarIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  value: valueProp,
  onChange,
  options = defaultOptions,
  variant = 'default',
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [internalValue, setInternalValue] = useState<TimeRange>('month');
  const isControlled = valueProp !== undefined && valueProp !== null;
  const value = isControlled ? valueProp : internalValue;
  const handleSelect = (option: TimeRange) => {
    if (!isControlled) setInternalValue(option);
    if (typeof onChange === 'function') onChange(option);
  };
  const labels: Record<TimeRange, string> = {
    today: 'Today',
    week: 'Week',
    month: 'Month',
    quarter: 'Quarter',
    year: 'Year',
    all: 'All',
  };

  const isCompact = variant === 'compact';

  return (
    <div
      className={`inline-flex items-center rounded-xl border ${
        isDark
          ? 'border-slate-600 bg-slate-800/60'
          : 'border-slate-200 bg-white/80 shadow-sm'
      } ${isCompact ? 'p-0.5' : 'p-1'}`}
    >
      {options.map((option) => {
        const isSelected = value === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => handleSelect(option)}
            className={`rounded-lg text-sm font-medium transition-all duration-150 ${
              isCompact ? 'px-2.5 py-1.5' : 'px-3 py-2'
            } ${
              isSelected
                ? isDark
                  ? 'bg-teal-500/20 text-teal-400 shadow-none'
                  : 'bg-teal-50 text-teal-700 shadow-sm'
                : isDark
                  ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            {labels[option]}
          </button>
        );
      })}
    </div>
  );
};

export default TimeRangeSelector;
export { CalendarIcon };

